import React, { useState, useEffect } from 'react';
import { Carousel, Grid } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import BannerImage from './BannerImage';

const { useBreakpoint } = Grid;
const CONTENT_WIDTH = 1240; // 中間那張的寬度（與下方商品區一致）

// Determine whether a link is internal (react-router) or external (<a>).
function isExternal(link) {
  return /^(https?:)?\/\//i.test(link) || link.startsWith('mailto:') || link.startsWith('tel:');
}

// 自訂左右箭頭（antd 內建箭頭常常看不到，改用明確可見的圓形按鈕）。
// offset＝中間那張距離視窗邊緣的留白（centerPadding），讓箭頭貼在中間圖的左右側。
function HeroArrow({ onClick, dir, offset = 0 }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === 'left' ? '上一張' : '下一張'}
      className="hero-arrow"
      style={{ [dir]: (offset || 0) + 12 }}
    >
      {dir === 'left' ? <LeftOutlined /> : <RightOutlined />}
    </button>
  );
}

/**
 * HeroSlider — 滿版輪播：電腦版用 centerMode，中間那張寬度＝內容寬(1240)，
 * 左右兩張露出並淡出；手機版單張滿版。Renders nothing if there are no slides.
 */
export default function HeroSlider({ slides }) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // centerPadding = (視窗寬 - 1240) / 2 → 中間張剛好 1240，兩側露出鄰張
  const [centerPad, setCenterPad] = useState(0);
  useEffect(() => {
    const calc = () => {
      const w = document.documentElement.clientWidth || window.innerWidth;
      setCenterPad(Math.max(0, Math.round((w - CONTENT_WIDTH) / 2)));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  const safeSlides = Array.isArray(slides) ? slides.filter((s) => s && s.image) : [];
  if (safeSlides.length === 0) return null;

  const height = isMobile ? 220 : 360;
  const pad = isMobile ? 0 : '0 10px'; // 中間與兩側之間的間距

  const renderSlide = (slide, idx) => {
    const inner = (
      <BannerImage src={slide.image} height={height} radius={0} style={{ display: 'flex', alignItems: 'flex-end' }}>
        {slide.title ? (
          <div
            style={{
              position: 'relative',
              width: '100%',
              padding: isMobile ? '16px 18px' : '28px 40px',
              background: 'linear-gradient(to top, rgba(20,24,52,0.55), rgba(20,24,52,0))',
              color: '#fff',
            }}
          >
            <span
              style={{
                display: 'block',
                fontSize: isMobile ? 18 : 28,
                fontWeight: 800,
                letterSpacing: 0.5,
                textShadow: '0 2px 8px rgba(0,0,0,0.35)',
              }}
            >
              {slide.title}
            </span>
          </div>
        ) : null}
      </BannerImage>
    );

    if (!slide.link) {
      return <div key={idx} style={{ padding: pad, boxSizing: 'border-box' }}>{inner}</div>;
    }
    if (isExternal(slide.link)) {
      return (
        <a key={idx} href={slide.link} target="_blank" rel="noopener noreferrer" style={{ display: 'block', padding: pad, boxSizing: 'border-box' }}>
          {inner}
        </a>
      );
    }
    return (
      <Link key={idx} to={slide.link} style={{ display: 'block', padding: pad, boxSizing: 'border-box' }}>
        {inner}
      </Link>
    );
  };

  return (
    <div className={`hero-slider${isMobile ? ' hero-slider-mobile' : ''}`} style={{ position: 'relative', overflow: 'hidden' }}>
      <style>{`
        .hero-slider .slick-dots li button { background: rgba(255,255,255,0.55); }
        .hero-slider .slick-dots li.slick-active button { background: #fff; }
        /* 自訂圓形箭頭 */
        .hero-arrow {
          position: absolute; top: 50%; transform: translateY(-50%); z-index: 5;
          width: 46px; height: 46px; border-radius: 50%; border: none; cursor: pointer;
          background: rgba(0,0,0,0.42); color: #fff; font-size: 18px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 2px 12px rgba(0,0,0,0.3); transition: background .2s;
        }
        .hero-arrow:hover { background: rgba(0,0,0,0.62); }
        /* 左右兩張漸進漸出；中間張全亮 */
        .hero-slider .slick-slide { opacity: 0.4; transition: opacity .45s ease; }
        .hero-slider .slick-center { opacity: 1; }
        .hero-slider.hero-slider-mobile .slick-slide { opacity: 1; }
      `}</style>
      <Carousel
        autoplay
        autoplaySpeed={4500}
        dots
        draggable
        arrows={!isMobile}
        prevArrow={<HeroArrow dir="left" offset={centerPad} />}
        nextArrow={<HeroArrow dir="right" offset={centerPad} />}
        infinite
        slidesToShow={1}
        {...(isMobile ? {} : { centerMode: true, centerPadding: `${centerPad}px` })}
      >
        {safeSlides.map(renderSlide)}
      </Carousel>
    </div>
  );
}
